/** Pure helpers for the Articles screen of the Visitors Desk. */
import type { ArticleRow, Ranking } from '../api/visitorsDesk'

export const RANKING_BUTTONS: { id: Ranking; label: string }[] = [
  { id: 'most_read', label: 'Most read' },
  { id: 'brings_in', label: 'Brings people in' },
  { id: 'keeps_members', label: 'Keeps members' },
  { id: 'converts', label: 'Turns readers into members' },
]

export interface StackedPart { key: 'anonymous' | 'known' | 'member'; label: string; value: number; color: string }

/** Neutral greys, heavier as the reader is more known. */
const WHO_COLORS = { anonymous: '#5d5a52', known: '#9a9488', member: '#e8e2d4' } as const

/** The who-read-it split, always anonymous, known, member, each with its legend label. */
export function whoParts(who: ArticleRow['who']): StackedPart[] {
  return [
    { key: 'anonymous', label: 'Anonymous', value: who.anonymous, color: WHO_COLORS.anonymous },
    { key: 'known', label: 'Known', value: who.known, color: WHO_COLORS.known },
    { key: 'member', label: 'Members', value: who.member, color: WHO_COLORS.member },
  ]
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** `History · Oct 9 · free`. The date is read from the ISO text, so no timezone can move it. */
export function metaLine(row: Pick<ArticleRow, 'section' | 'published' | 'access'>): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(row.published ?? '')
  const when = m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}` : null
  return [row.section, when, row.access].filter(Boolean).join(' · ')
}

export interface ArticleFilters { section?: string; published_from?: string; published_to?: string; access?: 'free' | 'members' }
const FILTER_KEYS = ['section', 'published_from', 'published_to', 'access'] as const

export function articleFiltersFromSearch(sp: URLSearchParams): ArticleFilters {
  const out: ArticleFilters = {}
  const section = sp.get('section')
  if (section) out.section = section
  const from = sp.get('published_from')
  if (from && /^\d{4}-\d{2}-\d{2}$/.test(from)) out.published_from = from
  const to = sp.get('published_to')
  if (to && /^\d{4}-\d{2}-\d{2}$/.test(to)) out.published_to = to
  const access = sp.get('access')
  if (access === 'free' || access === 'members') out.access = access
  return out
}

/** Search params with the article filters replaced by `f`; everything else stays. */
export function withArticleFilters(spKey: string, f: ArticleFilters): URLSearchParams {
  const next = new URLSearchParams(spKey)
  for (const k of FILTER_KEYS) next.delete(k)
  for (const k of FILTER_KEYS) if (f[k]) next.set(k, f[k]!)
  return next
}

export function rankingFromSearch(sp: URLSearchParams): Ranking {
  const r = sp.get('ranking')
  return RANKING_BUTTONS.find((b) => b.id === r)?.id ?? 'most_read'
}

export const th = 'px-2 py-1.5 text-left font-mono text-[11px] font-semibold uppercase tracking-wide text-paper-faint'
export const td = 'px-2 py-1.5 align-top'
export const mono = { fontVariantNumeric: 'tabular-nums' } as const
